import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Api, ApiConfig } from 'auxilium-connect';
import { Observable, of } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import 'socket.io-client';
import { environment } from 'src/environments/environment';

// needed to get our rigged library in here
type ClassRef<T> = new (config: ApiConfig) => T;
declare global {
  interface Window {
    Api: ClassRef<Api>;
    api: (req: any, cb?: (...args: any[]) => void) => void;
    syncData?: string;
  }
}

@Injectable()
export class ApiService extends window.Api {
  originalAuthenticateFn = null;
  originalIsValidFn = null;
  private _interfaceSyncKey = 'api.last-interface-sync';
  private _interfaceStoreKey = 'api.last-interface-sync-result';
  private _lastInterfaceMapCacheKey = 'api.last-interface-map-cache';
  private _syncThreshold = 1000 * 60 * 60; // 1 hour?
  constructor(
    private _snack: MatSnackBar,
    @Inject(DOCUMENT) private _doc: Document,
    private router: Router
  ) {
    super(environment.api);
    if (!window.api) {
      window.api = (req, cb) => {
        this.request(req).subscribe((resp) => (cb || console.log)(resp));
      };
    }

    this._runChecks();
    this.myAuthenticate();
  }

  myAuthenticate() {
    this.originalAuthenticateFn = this.authenticate;
    this.originalIsValidFn = this.isValidUser;
    this.isValidUser = () => {
      if (document.URL.indexOf('online') > -1) {
        this.allowGuest = true;
        environment.api.allowGuest = true;
        if (this.allowGuest || this.getToken()) {
          return of(true);
        } else {
          this.showLogin().subscribe();
          localStorage.clear();
          return of(false);
        }
      } else {
        this.allowGuest = false;
        environment.api.allowGuest = false;
        return this.originalIsValidFn();
      }
    };
  }

  // used in your routes, will wrap a login request if the passed function that generates
  // a observable<boolean> resolves false, and will monitor until a successful change
  // has been detected.
  // be careful of the 'scope' argument - if you're calling something located in
  // a service, you'll likely need to pass the service instance as the scope
  routeGuardForceLogin(guard: () => Observable<boolean>, scope: any = this) {
    return guard.apply(scope).pipe(
      switchMap((valid) => {
        if (valid) {
          return of(true);
        } else {
          const cur = this.getCurrentAuth() || { id: -1 };
          this.showLogin().subscribe();
          if (document.URL.indexOf('pos') > -1) {
            this.auth.subscribe((r) => {
              if (!r.guest) {
                this.router.navigateByUrl('/pos');
              }
            });
          }
          return this.auth.pipe(
            filter((a) => a.id !== cur.id),
            switchMap(() => guard.apply(scope)),
            filter((a) => !!a) // and ensure that it passed
          );
        }
      })
    );
  }

  fetchInterfaces(
    sliceMap: { [sliceId: number]: string } = environment.sliceMap || {},
    force?: boolean,
    includeSystem?: boolean
  ) {
    if (!environment.production) {
      const admin = !!this.getCurrentAuth()?.sysadmin,
        lastChecked = new Date(
          localStorage.getItem(this._interfaceSyncKey)
        ).getTime(),
        now = new Date().getTime(),
        lastMap = localStorage.getItem(this._lastInterfaceMapCacheKey),
        curMap = JSON.stringify(sliceMap),
        cacheChanged = lastMap !== curMap;

      if (
        admin &&
        (cacheChanged || force || now - lastChecked > this._syncThreshold)
      ) {
        let names: Map<number, string>;

        console.warn('Sync threshold exceed, pulling in interfaces...');
        console.warn('(This request may take a while...)');
        return this.request<{ rows: { id: number; source_table: string }[] }>(
          {
            '$/slice/permissions_lite': {
              where: [
                '$and',
                ['$empty', ['$field', 'parent']],
                includeSystem
                  ? true
                  : ['$like', ['$field', 'source_db'], '%user_tables']
              ]
            }
          },
          { labelAs: 'fetchInterfaces.manifest' }
        ).pipe(
          map((resp) =>
            resp.rows.map(
              (row) => [row.id, row.source_table] as [number, string]
            )
          ),
          tap((list) => (names = new Map(list))),
          switchMap((rows) => {
            const reqs: { [sliceId: number]: any } = {};

            rows
              .map(([slice, name]) => ({
                [slice]: {
                  '$/slice/report': { slice, return: { types: true } },
                  $pop: 'types'
                }
              }))
              .forEach((req) => Object.assign(reqs, req));

            return this.request<{
              [slice: number]: {
                [field: string]: { native: string; user: string };
              };
            }>(reqs, { labelAs: 'fetchInterfaces.types' });
          }),
          map((resp) => {
            const interfaces: string[] = [],
              enums: string[] = [];
            Object.entries(resp).forEach(([id, types]) => {
              const slice = +id,
                name = names
                  .get(slice)
                  .replace(/[^a-z0-9+]+/gi, ' ')
                  .replace(/\s\s+/g, ' ')
                  .replace(/\s/g, '_'),
                customName = sliceMap?.[slice],
                interfaceName = customName || `S_${name}`,
                defs: string[] = [];
              Object.keys(types)
                .map(
                  (key) =>
                    [key, types[key]] as [
                      string,
                      { native: string; user: string }
                    ]
                )
                .forEach(([key, def]) => {
                  switch (def.user) {
                    case 'int':
                    case 'decimal':
                      defs.push(`\t\t'${key}'?: number;`);
                      break;
                    case 'text':
                    case 'paragraph':
                      defs.push(`\t\t'${key}'?: string;`);
                      break;
                    case 'date':
                    case 'datetime':
                      defs.push(`\t\t'${key}'?: Date;`);
                      break;
                    default:
                      console.warn('unhandled', key, def);
                  }
                });
              interfaces.push(
                `\t/* ${name} */\n\texport interface ${interfaceName} {\n${defs.join(
                  '\n'
                )}\n\t}\n`
              );
              enums.push(`\t\t${interfaceName} = ${slice},`);

              localStorage.setItem(
                this._interfaceSyncKey,
                `${new Date().getTime()}`
              );
            });
            const retVal = `/* auto generated on ${new Date()} */\n\n\nexport namespace SLICES {\n\texport enum IDS {\n${enums.join(
              '\n'
            )}\n\t}\n\n\n${interfaces.join('\n')}}`;
            localStorage.setItem(this._interfaceStoreKey, retVal);
            localStorage.setItem(this._lastInterfaceMapCacheKey, curMap);
            window.syncData = retVal;
            console.info(
              `data is available on 'window.syncData', and also available in localstorage (${this._interfaceStoreKey})`
            );
            return retVal;
          })
        );
      } else if (admin) {
        console.warn(
          `under sync threshold (${this._syncThreshold}ms), skipping.`
        );
        console.info(
          `you can remove the ${this._interfaceSyncKey} from localStorage and refresh to autogenerate`
        );
      }
      return of<string>(localStorage.getItem(this._interfaceStoreKey));
    }
    return of<string>(null);
  }

  private _runChecks() {
    const messages = [this._checkApiHost()].filter((x) => !!x);

    if (messages.length) {
      console.warn('runChecks had this to say:\n', messages);
      this._snack.open(messages.join('.  '));
    }
  }

  private _checkApiHost(): string {
    const target = 'https://datalynk.ca/v1/api.js',
      n = this._doc.head.querySelector(`script[src="${target}"]`);
    return n ? null : `api.js is NOT being loaded from ${target}`;
  }
}
